import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import OtpInput from '../auth/OtpInput';
import MaskedAadhaarInput from '../common/MaskedAadhaarInput';
import PrimaryButton from '../auth/PrimaryButton';
import ResendOtp from '../auth/ResendOtp';
import {
  requestVerificationOTP,
  confirmVerificationOTP,
  selectVerificationABHA,
  getAbhaAddressSuffix,
} from '../../services/api/abhaApi';
import { isCaptchaError } from '../../utils/abhaCaptcha';
import useOtpResend from '../../hooks/useOtpResend';
import useAbhaCaptcha from '../../hooks/useAbhaCaptcha';
import CaptchaField from './CaptchaField';
import { isValidAadhaar, AADHAAR_INVALID_MESSAGE } from '../../utils/aadhaar';
import { VERIFY_FLOW, SKIP_ABHA, NO_MORE_RESENDS, CAPTCHA_COPY } from '../../constants/abdmCopy';

/**
 * ABHAVerificationFlow — verify an existing ABHA via 3 login methods.
 *
 * abha-number / aadhaar: 2 steps (enter ID → OTP → profile)
 * mobile:                3 steps (enter mobile → OTP → pick ABHA → profile)
 *
 * Props:
 *   onComplete({ txnId, abhaNumber, abhaAddress, name, gender, yearOfBirth, mobile })
 *   onSkip()
 *   requireAbhaNumber — when true, a profile without an ABHA number is rejected (no onComplete)
 *   abhaAddressSuffix — suffix for the ABHA Address hint (default: from the ABDM environment, once)
 *   onCreateAbha()    — optional; when given, "no ABHA found" errors offer a "Create ABHA" button
 */

const METHODS = [
  { key: 'mobile',      label: VERIFY_FLOW.mobileTab, placeholder: '10-digit mobile',    maxRaw: 10, minRaw: 10 },
  { key: 'aadhaar',     label: 'Aadhaar',     placeholder: '12-digit Aadhaar',   maxRaw: 12, minRaw: 12 },
  { key: 'abha-number', label: 'ABHA Number', placeholder: '91-XXXX-XXXX-XXXX', maxRaw: 14, minRaw: 14 },
  { key: 'abha-address', label: 'ABHA Address', placeholder: 'name@abdm', minRaw: 1 },
];

// Tabs that let the user pick which mobile receives the OTP.
const OTP_CHOICE_METHODS = ['abha-number', 'abha-address'];
const OTP_METHOD_MOBILE = 'mobile';
const OTP_METHOD_AADHAAR = 'aadhaar';

export const MOBILE_INVALID_MESSAGE = 'Please enter a valid mobile number';
const MOBILE_RE = /^[6-9]\d{9}$/;
// Contract error codes (§2.11) that mean "this identifier has no ABHA" → offer Create ABHA.
const NOT_FOUND_CODES = ['ABHA_NOT_FOUND_MOBILE', 'NO_ABHA_FOR_AADHAAR'];

export const NO_ABHA_NUMBER_MESSAGE =
  "This ABHA address has no ABHA number, so it can't be linked to a patient record yet.";

// Mask an ABHA number for DISPLAY only — keep the full value for the select call. 14-digit ABHA
// (2-4-4-4) → "91-XXXX-XXXX-1234"; otherwise show only the last 4.
const maskAbhaNumber = (n) => {
  const d = (n || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.length === 14) return `${d.slice(0, 2)}-XXXX-XXXX-${d.slice(-4)}`;
  return `••••${d.slice(-4)}`;
};

// ── Helpers ────────────────────────────────────────────────────────────

function inputStyle(isDark, hasError = false) {
  return {
    width: '100%',
    padding: '10px 12px',
    fontSize: 14,
    border: `1.5px solid ${hasError ? '#ef4444' : isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
    borderRadius: 8,
    background: isDark ? 'rgba(255,255,255,0.06)' : '#f9fafb',
    color: isDark ? '#f5f5f7' : '#111827',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };
}

function labelStyle(isDark) {
  return {
    display: 'block',
    marginBottom: 4,
    fontSize: 12,
    fontWeight: 600,
    color: isDark ? 'rgba(255,255,255,0.55)' : '#6b7280',
    letterSpacing: '0.2px',
  };
}

function ErrorBanner({ error, onSkip, onCreateAbha, isDark }) {
  if (!error) return null;
  return (
    <div style={{
      borderRadius: 8, padding: '10px 12px', marginBottom: 14,
      border: `1px solid ${isDark ? '#7f1d1d' : '#fca5a5'}`,
      background: isDark ? 'rgba(127,29,29,0.35)' : '#fef2f2',
    }}>
      <p style={{ fontSize: 13, color: isDark ? '#fca5a5' : '#b91c1c', margin: 0, marginBottom: error.allowSkip ? 8 : 0 }}>
        {error.userMessage}
      </p>
      {onCreateAbha && NOT_FOUND_CODES.includes(error.abhaCode) && (
        <button
          type="button"
          onClick={onCreateAbha}
          style={{
            fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 5, marginRight: 8,
            border: 'none', background: '#0d9488', color: '#fff', cursor: 'pointer',
          }}
        >
          Create ABHA
        </button>
      )}
      {error.allowSkip && (
        <button
          onClick={onSkip}
          style={{
            fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 5,
            border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
            background: isDark ? '#1e293b' : '#fff',
            color: isDark ? '#94a3b8' : '#64748b', cursor: 'pointer',
          }}
        >
          {SKIP_ABHA}
        </button>
      )}
    </div>
  );
}

// ── Auto-format ABHA number as user types ─────────────────────────────
function formatAbha(raw) {
  const d = raw.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `${d.slice(0, 2)}-${d.slice(2)}`;
  if (d.length <= 10) return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
  return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6, 10)}-${d.slice(10)}`;
}

// ── Main component ─────────────────────────────────────────────────────

export default function ABHAVerificationFlow({
  onComplete, onSkip, defaultMobile = '', initialMethod = null, initialLoginInput = '',
  requireAbhaNumber = false, abhaAddressSuffix, onCreateAbha,
  title = VERIFY_FLOW.title,
  subtitle = VERIFY_FLOW.subtitle,
}) {
  const { isDark } = useTheme();

  const _mobileFromParent = String(defaultMobile || '').replace(/\D/g, '').slice(0, 10);
  // When the registration/booking mobile is known, default to Mobile verify with it pre-filled.
  const [method, setMethod]                   = useState(initialMethod || 'mobile');
  const [loginInput, setLoginInput]           = useState(initialLoginInput || _mobileFromParent || '');
  const [step, setStep]                       = useState('input');   // 'input' | 'otp' | 'picker'
  const [txnId, setTxnId]                     = useState('');
  const [otp, setOtp]                         = useState('');
  const [mappedList, setMappedList]           = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [error, setError]                     = useState(null);
  const [otpMethod, setOtpMethod]             = useState(OTP_METHOD_MOBILE);
  const [maskedMobile, setMaskedMobile]       = useState(null);
  const { secondsLeft, canResend, resendsLeft, markSent, reset } = useOtpResend();

  // Suffix default comes from the ABDM environment (single-flight + cached in abhaApi); a prop wins.
  const [envSuffix, setEnvSuffix] = useState('@abdm');
  useEffect(() => {
    if (abhaAddressSuffix) return undefined;
    let live = true;
    getAbhaAddressSuffix().then((sfx) => { if (live) setEnvSuffix(sfx); });
    return () => { live = false; };
  }, [abhaAddressSuffix]);
  const suffix = abhaAddressSuffix || envSuffix;

  // Mobile method is bound to the top-level registration/booking number — keep it in sync and
  // locked (re-populates when switching back to Mobile or if the number above changes).
  useEffect(() => {
    if (method === 'mobile' && _mobileFromParent) setLoginInput(_mobileFromParent);
  }, [method, _mobileFromParent]);

  const currentMethod = METHODS.find((m) => m.key === method);

  // Derive raw digits / validated login ID
  const rawDigits = loginInput.replace(/\D/g, '');
  const mobileInvalid = method === 'mobile' && rawDigits.length > 0
    && (/^[0-5]/.test(rawDigits) || (rawDigits.length === 10 && !MOBILE_RE.test(rawDigits)));
  const aadhaarInvalid = method === 'aadhaar' && rawDigits.length === 12 && !isValidAadhaar(rawDigits);
  const isValid = method === 'abha-address'
    ? loginInput.trim().length > 0
    : rawDigits.length === currentMethod.minRaw && !mobileInvalid && !aadhaarInvalid;

  // Display value (auto-format ABHA number with dashes, rest as-is)
  const displayInput = method === 'abha-number' ? formatAbha(rawDigits) : loginInput;

  // Raw value to send to the API (no dashes)
  const loginId = method === 'abha-number' ? rawDigits : loginInput.trim();
  const sendsOtpMethod = OTP_CHOICE_METHODS.includes(method);
  const otpMethodArg = sendsOtpMethod ? otpMethod : undefined;

  // VRFY_ABHA_301: a captcha before the first send by COMMUNICATION MOBILE only (the one place the M1 sheet
  // asks for it), bound to exactly what that send carries. Other tabs send without one.
  const needsCaptcha = method === 'mobile';
  const captchaFields = useMemo(() => ({ login_hint: 'mobile', login_id: loginId }), [loginId]);
  const captcha = useAbhaCaptcha('verify', captchaFields, needsCaptcha && step === 'input' && isValid);
  const captchaReady = !needsCaptcha || captcha.ready;
  const [captchaError, setCaptchaError] = useState(null);

  const handleInputChange = (e) => {
    if (method === 'abha-number') {
      setLoginInput(e.target.value.replace(/[^0-9]/g, '').slice(0, 14));
    } else if (method === 'abha-address') {
      setLoginInput(e.target.value.toLowerCase().replace(/\s/g, ''));
    } else if (method === 'aadhaar') {
      setLoginInput(e.target.value.replace(/\D/g, '').slice(0, 12));
    } else {
      setLoginInput(e.target.value.replace(/\D/g, '').slice(0, 10));
    }
    if (error) setError(null);
    setCaptchaError(null);
  };

  const handleMethodChange = (key) => {
    setMethod(key);
    setLoginInput('');
    setOtpMethod(OTP_METHOD_MOBILE);
    reset();
    setStep('input');
    setOtp('');
    setMappedList([]);
    setError(null);
    setCaptchaError(null);
    setTxnId('');
  };

  // Step 1 — request OTP
  const handleRequestOTP = useCallback(async () => {
    if (!isValid || loading || !captchaReady) return;
    setLoading(true);
    setError(null);
    setCaptchaError(null);
    try {
      const { txnId: tid, maskedMobile: masked } = await requestVerificationOTP({
        loginHint: method, loginId, otpMethod: otpMethodArg, ...(needsCaptcha ? { captcha: captcha.payload } : {}),
      });
      setTxnId(tid);
      setMaskedMobile(masked || null);
      markSent();
      setStep('otp');
    } catch (err) {
      if (isCaptchaError(err)) setCaptchaError(err);
      else setError(err);
      if (needsCaptcha) captcha.refresh();   // the server may have spent this one: always offer a new code
    } finally {
      setLoading(false);
    }
  }, [isValid, loading, method, loginId, otpMethodArg, markSent, captcha, needsCaptcha, captchaReady]);

  const handleResendOTP = useCallback(async () => {
    if (!canResend || loading) return;
    setLoading(true);
    setError(null);
    try {
      const { txnId: tid, maskedMobile: masked } = await requestVerificationOTP({
        loginHint: method, loginId, otpMethod: otpMethodArg, resend: true,
      });
      setTxnId(tid);
      if (masked) setMaskedMobile(masked);
      markSent();
    } catch (err) {
      if (err.abhaCode === 'CAPTCHA_REQUIRED') {
        // The server no longer has a live send for this number: back to the first send, number kept.
        setStep('input');
        setOtp('');
        reset();
        setCaptchaError({ userMessage: CAPTCHA_COPY.startAgain });
        return;
      }
      if (err.abhaCode === 'OTP_RESEND_TOO_SOON') markSent(err.retryAfter);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [canResend, loading, method, loginId, otpMethodArg, markSent, reset]);

  // O2: an ABHA-address login can return a profile with no ABHA number.
  const completeProfile = useCallback((result) => {
    if (requireAbhaNumber && !result?.abhaNumber) {
      setError({ userMessage: NO_ABHA_NUMBER_MESSAGE });
      setOtp('');
      return;
    }
    onComplete(result);
  }, [requireAbhaNumber, onComplete]);

  // Step 2 — verify OTP
  const handleVerifyOTP = useCallback(async () => {
    if (otp.length !== 6 || loading) return;
    setLoading(true);
    setError(null);
    try {
      const result = await confirmVerificationOTP({
        txnId,
        otp,
        abhaNumber: method === 'abha-number' ? loginId : '',
      });
      if (result.mappedAbhaList && result.mappedAbhaList.length > 0) {
        // Mobile flow — show picker
        setMappedList(result.mappedAbhaList);
        setStep('picker');
      } else {
        completeProfile(result);
      }
    } catch (err) {
      setError(err);
      setOtp('');
    } finally {
      setLoading(false);
    }
  }, [txnId, otp, loading, method, loginId, completeProfile]);

  // Step 3 (mobile) — select ABHA from list
  const handleSelectABHA = useCallback(async (abhaNumber) => {
    setLoading(true);
    setError(null);
    try {
      const result = await selectVerificationABHA({ txnId, abhaNumber });
      completeProfile(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [txnId, completeProfile]);

  // ── Styles ─────────────────────────────────────────────────────────
  const panelStyle = {
    borderRadius: 12,
    border: `1px solid ${isDark ? 'rgba(20,184,166,0.3)' : '#99f6e4'}`,
    background: isDark ? '#1e293b' : '#f0fdfa',
    padding: '20px 20px 16px',
  };

  const secondaryBtn = {
    width: '100%',
    marginTop: 8,
    padding: '9px 12px',
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 8,
    border: `1px solid ${isDark ? '#334155' : '#cbd5e1'}`,
    background: isDark ? '#0f172a' : '#fff',
    color: isDark ? '#94a3b8' : '#64748b',
    cursor: 'pointer',
  };

  // OTP step hint text
  const viaAadhaar = method === 'aadhaar' || (sendsOtpMethod && otpMethod === OTP_METHOD_AADHAAR);
  const otpHint = maskedMobile
    ? `OTP sent to ${viaAadhaar ? 'Aadhaar' : 'ABHA'} linked mobile number ending with ${maskedMobile}`
    : (viaAadhaar ? VERIFY_FLOW.otpSentAadhaarMobile : VERIFY_FLOW.otpSentAbhaMobile);

  return (
    <div style={panelStyle}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: isDark ? '#2dd4bf' : '#0f766e' }}>
            {title}
          </h3>
          <p style={{ margin: '3px 0 0', fontSize: 12, color: isDark ? '#64748b' : '#94a3b8' }}>
            {subtitle}
          </p>
        </div>
        <button onClick={onSkip} style={{
          fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6,
          border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
          background: isDark ? '#1e293b' : '#fff',
          color: isDark ? '#64748b' : '#94a3b8', cursor: 'pointer', flexShrink: 0,
        }}>
          Skip
        </button>
      </div>

      {/* Method toggle — only shown on input step */}
      {step === 'input' && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {METHODS.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => handleMethodChange(m.key)}
              style={{
                flex: 1, padding: '6px 8px', fontSize: 12, fontWeight: 600,
                borderRadius: 7, cursor: 'pointer', textAlign: 'center',
                border: `1.5px solid ${method === m.key
                  ? (isDark ? 'rgba(20,184,166,0.6)' : '#2dd4bf')
                  : (isDark ? '#334155' : '#d1d5db')}`,
                background: method === m.key
                  ? (isDark ? 'rgba(20,184,166,0.15)' : '#f0fdfa')
                  : (isDark ? '#0f172a' : '#fff'),
                color: method === m.key
                  ? (isDark ? '#2dd4bf' : '#0f766e')
                  : (isDark ? '#94a3b8' : '#6b7280'),
              }}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}

      <ErrorBanner error={error} onSkip={onSkip} onCreateAbha={onCreateAbha} isDark={isDark} />

      {/* ── Step: input ── */}
      {step === 'input' && (
        <div>
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle(isDark)}>{currentMethod.label}</label>
            {method === 'aadhaar' ? (
              <MaskedAadhaarInput
                value={displayInput}
                onChange={handleInputChange}
                placeholder={currentMethod.placeholder}
                style={inputStyle(isDark)}
                isDark={isDark}
              />
            ) : method === 'abha-address' ? (
              <input
                type="text"
                value={loginInput}
                onChange={handleInputChange}
                placeholder={currentMethod.placeholder}
                style={inputStyle(isDark)}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
              />
            ) : (() => {
              const lockMobile = method === 'mobile' && Boolean(_mobileFromParent);
              return (
                <input
                  type="text"
                  inputMode="numeric"
                  value={displayInput}
                  onChange={(e) => { if (!lockMobile) handleInputChange(e); }}
                  placeholder={currentMethod.placeholder}
                  readOnly={lockMobile}
                  disabled={lockMobile}
                  style={{ ...inputStyle(isDark), ...(lockMobile ? { opacity: 0.75, cursor: 'not-allowed' } : {}) }}
                  autoComplete="off"
                />
              );
            })()}
            {method === 'mobile' && _mobileFromParent && (
              <p style={{ fontSize: 11, marginTop: 4, color: isDark ? '#94a3b8' : '#64748b' }}>
                Using the mobile number from registration (change it above)
              </p>
            )}
            {mobileInvalid && (
              <p role="alert" style={{ fontSize: 11, marginTop: 4, color: '#ef4444' }}>{MOBILE_INVALID_MESSAGE}</p>
            )}
            {aadhaarInvalid && (
              <p role="alert" style={{ fontSize: 11, marginTop: 4, color: '#ef4444' }}>{AADHAAR_INVALID_MESSAGE}</p>
            )}
            {method === 'abha-number' && rawDigits.length > 0 && rawDigits.length < 14 && (
              <p style={{ fontSize: 11, marginTop: 4, color: isDark ? '#fbbf24' : '#d97706' }}>
                {VERIFY_FLOW.abhaNumberHint}
              </p>
            )}
            {method === 'abha-address' && (
              <p style={{ fontSize: 11, marginTop: 4, color: isDark ? '#94a3b8' : '#64748b' }}>
                e.g. name{suffix}
              </p>
            )}
            {sendsOtpMethod && (
              <div style={{ marginTop: 12 }}>
                <label htmlFor="abha-otp-method" style={labelStyle(isDark)}>{VERIFY_FLOW.sendOtpTo}</label>
                <select
                  id="abha-otp-method"
                  value={otpMethod}
                  onChange={(e) => setOtpMethod(e.target.value)}
                  style={inputStyle(isDark)}
                >
                  <option value={OTP_METHOD_MOBILE}>{VERIFY_FLOW.optAbhaMobile}</option>
                  <option value={OTP_METHOD_AADHAAR}>{VERIFY_FLOW.optAadhaarMobile}</option>
                </select>
              </div>
            )}
          </div>
          {needsCaptcha && (
            <CaptchaField captcha={captcha} error={captchaError} isDark={isDark} disabled={loading} id="abha-verify-captcha" />
          )}
          <PrimaryButton onClick={handleRequestOTP} disabled={!isValid || loading || !captchaReady}>
            {loading ? 'Sending OTP…' : `Send OTP`}
          </PrimaryButton>
        </div>
      )}

      {/* ── Step: otp ── */}
      {step === 'otp' && (
        <div>
          {method === 'mobile' && (
            <p style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#64748b', marginBottom: 4, marginTop: 0 }}>
              ABHA Exists to communication Mobile
            </p>
          )}
          <p style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#64748b', marginBottom: 14, marginTop: 0 }}>
            {otpHint}
          </p>

          <OtpInput value={otp} onChange={setOtp} disabled={loading} />

          <div style={{ marginTop: 10, marginBottom: 16 }}>
            {resendsLeft > 0 ? (
              <ResendOtp
                onClick={handleResendOTP}
                disabled={!canResend || loading}
                seconds={secondsLeft}
              />
            ) : (
              <p style={{ fontSize: 12, margin: 0, color: isDark ? '#94a3b8' : '#64748b' }}>
                {NO_MORE_RESENDS}
              </p>
            )}
          </div>

          <PrimaryButton onClick={handleVerifyOTP} disabled={otp.length !== 6 || loading}>
            {loading ? 'Verifying…' : 'Verify & Continue'}
          </PrimaryButton>

          <button
            onClick={() => { setStep('input'); setOtp(''); setError(null); reset(); }}
            style={secondaryBtn}
          >
            ← Change {currentMethod.label}
          </button>
        </div>
      )}

      {/* ── Step: picker (mobile flow only) ── */}
      {step === 'picker' && (
        <div>
          <p style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#64748b', marginBottom: 14, marginTop: 0 }}>
            {mappedList.length === 1
              ? '1 ABHA found for this mobile'
              : `${mappedList.length} ABHAs found — choose one`}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {mappedList.map((item, idx) => {
              const abhaNum = item.ABHANumber || item.abhaNumber || item.healthId || '';
              const status  = item.status || item.Status || '';
              const name    = item.name || item.fullName || '';
              const address = item.preferredAbhaAddress || '';
              const photo   = item.profilePhoto || '';
              return (
                <div
                  key={abhaNum || idx}
                  className="abha-picker-card"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8,
                    border: `1px solid ${isDark ? '#1e40af' : '#bae6fd'}`,
                    background: isDark ? '#1e2d3d' : '#f0f9ff',
                    color: isDark ? '#f1f5f9' : '#111827',
                    opacity: loading ? 0.6 : 1,
                  }}
                >
                  {photo && (
                    <img
                      src={photo.startsWith('data:') ? photo : `data:image/jpeg;base64,${photo}`}
                      alt={name ? `${name} photo` : 'ABHA photo'}
                      style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {name && <div style={{ fontWeight: 600, fontSize: 13 }}>{name}</div>}
                    {address && <div style={{ fontSize: 12, color: isDark ? '#94a3b8' : '#6b7280', marginTop: 2 }}>{address}</div>}
                    <div style={{ fontSize: 12, fontFamily: 'monospace', marginTop: 2 }}>{maskAbhaNumber(abhaNum)}</div>
                    {status && (
                      <div style={{
                        fontSize: 11, marginTop: 2, fontWeight: 600,
                        color: status.toUpperCase() === 'ACTIVE'
                          ? (isDark ? '#34d399' : '#059669')
                          : (isDark ? '#f87171' : '#dc2626'),
                      }}>
                        {status.toUpperCase() === 'ACTIVE' ? VERIFY_FLOW.statusActive : VERIFY_FLOW.statusInactive}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSelectABHA(abhaNum)}
                    disabled={loading}
                    onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(0.9)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.filter = ''; }}
                    onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 3px rgba(13,148,136,0.4)'; }}
                    onBlur={(e) => { e.currentTarget.style.boxShadow = ''; }}
                    style={{
                      flexShrink: 0, padding: '7px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8,
                      border: 'none', background: '#0d9488', color: '#fff',
                      cursor: loading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Use this ABHA
                  </button>
                </div>
              );
            })}
          </div>
          {loading && (
            <p style={{ fontSize: 13, color: isDark ? '#94a3b8' : '#6b7280', marginTop: 12 }}>
              Fetching profile…
            </p>
          )}
        </div>
      )}
    </div>
  );
}
